import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaUserFriends,
  FaInbox,
  FaSpinner,
  FaUserPlus,
  FaUserMinus,
  FaCheck,
  FaTimesCircle,
  FaEye,
  FaHandshake,
  FaCalendarAlt,
  FaProjectDiagram,
  FaStar,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import collaboratorsAPI from "../../services/collaboratorsAPI";
import { useUserContext } from "../../context/UserContext";
import CollabRequestModal from "../collaborators/CollabRequestModal";
import "./CollaboratorsModal.css";

// Individual collaboration item within a group
const CollaborationItem = React.memo(({ collaboration }) => (
  <div className="collaboration-detail-item">
    <div className="collaboration-info">
      <span className="collaboration-room">
        <FaProjectDiagram /> {collaboration.roomName}
      </span>
      <span className="collaboration-type">
        <FaHandshake /> {collaboration.collaborationType || "Research"}
      </span>
      <span className="collaboration-date">
        <FaCalendarAlt /> {new Date(collaboration.date).toLocaleDateString()}
      </span>
    </div>
  </div>
));

// Grouped collaborator component - shows person with all their collaborations
const CollaboratorGroupItem = React.memo(
  ({
    collaboratorGroup,
    isOwnProfile,
    currentUser,
    onSendRequest,
    isSendingRequest,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { person, collaborations, stats, timeline } = collaboratorGroup;

    return (
      <div className="profile-collab-group-item">
        <div
          className="profile-collab-group-header"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Link
            to={`/profile/${person.id}`}
            className="profile-collab-link"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                person.profilePhotoUrl ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  person.name || "User"
                )}&background=4f46e5&color=fff&size=50`
              }
              alt={person.name}
              className="profile-collab-avatar"
            />
            <div className="profile-collab-info">
              <div className="profile-collab-name-section">
                <h4>
                  {person.name}
                  {person.isVerified && (
                    <FaCheck className="profile-collab-verified" />
                  )}
                </h4>
                {person.averageRating > 0 && (
                  <div className="profile-collab-rating">
                    <FaStar className="rating-star" />
                    <span>{person.averageRating.toFixed(1)}</span>
                    <span className="rating-count">
                      ({person.totalRatings})
                    </span>
                  </div>
                )}
              </div>
              <p>
                {person.professionalRole || "Researcher"}
                {person.field && ` in ${person.field}`}
              </p>
              {person.relationship && (
                <div className="profile-collab-relationship">
                  {person.relationship.isFollowing && (
                    <span className="relationship-badge following">
                      Following
                    </span>
                  )}
                  {person.relationship.isFollowedBy && (
                    <span className="relationship-badge followed">
                      Follows you
                    </span>
                  )}
                </div>
              )}
            </div>
          </Link>

          <div className="profile-collab-summary">
            <div className="profile-collab-stats">
              <span className="collaborations-count">
                {stats?.collaborationCount || collaborations.length}{" "}
                collaboration
                {(stats?.collaborationCount || collaborations.length) !== 1
                  ? "s"
                  : ""}
              </span>
              {stats?.successRate && (
                <span className="success-rate">
                  {stats.successRate.toFixed(0)}% success
                </span>
              )}
              {stats?.averageCollaborationRating && (
                <span className="collab-rating">
                  <FaStar /> {stats.averageCollaborationRating.toFixed(1)}
                </span>
              )}
            </div>
            <span className={`expand-icon ${isExpanded ? "expanded" : ""}`}>
              ▼
            </span>
          </div>

          {!isOwnProfile && person.isOpenForCollaboration && (
            <button
              className={`profile-collab-btn ${
                isSendingRequest ? "profile-collab-btn-loading" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSendRequest(person.id);
              }}
              disabled={isSendingRequest}
            >
              {isSendingRequest ? (
                <FaSpinner className="profile-collab-spinner" />
              ) : (
                <FaHandshake />
              )}
              {isSendingRequest ? "Sending..." : "Collaborate"}
            </button>
          )}
        </div>

        {isExpanded && (
          <div className="collaborations-details">
            {timeline && (
              <div className="collaboration-timeline">
                <p>
                  <strong>First collaborated:</strong>{" "}
                  {new Date(timeline.firstCollaboratedAt).toLocaleDateString()}
                </p>
                <p>
                  <strong>Last collaborated:</strong>{" "}
                  {new Date(timeline.lastCollaboratedAt).toLocaleDateString()}
                </p>
              </div>
            )}
            <h5>Research Projects:</h5>
            {collaborations.map((collaboration) => (
              <CollaborationItem
                key={collaboration.id}
                collaboration={collaboration}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);

// Individual request item component
const RequestItem = React.memo(
  ({ request, onAccept, onReject, isProcessing, type, currentUserId }) => {
    // For incoming requests in profile modal, we always want to show the requester (sender)
    // For outgoing requests, we'd show the recipient, but profile modal only shows incoming
    const otherUser =
      type === "incoming" ? request.requester : request.recipient;

    return (
      <div className="profile-collab-request-item">
        <Link
          to={`/profile/${otherUser?.id || otherUser?._id}`}
          className="profile-collab-request-link"
        >
          <img
            src={
              otherUser?.profilePhotoUrl ||
              otherUser?.profilePhoto ||
              otherUser?.avatar ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                otherUser?.name ||
                  otherUser?.fullName ||
                  otherUser?.displayName ||
                  "User"
              )}&background=4f46e5&color=fff&size=50`
            }
            alt={otherUser?.name || otherUser?.fullName || "User"}
            className="profile-collab-request-avatar"
          />
          <div className="profile-collab-request-info">
            <h4>
              {otherUser?.name ||
                otherUser?.fullName ||
                otherUser?.displayName ||
                "Unknown User"}
            </h4>
            <p className="profile-collab-request-message">
              {request.message || "Wants to collaborate with you"}
            </p>
            <div className="profile-collab-request-details">
              {request.collaborationType && (
                <span className="profile-collab-request-type">
                  <FaHandshake /> {request.collaborationType}
                </span>
              )}
              {request.createdAt && (
                <span className="profile-collab-request-date">
                  <FaCalendarAlt />{" "}
                  {new Date(request.createdAt).toLocaleDateString()}
                </span>
              )}
              {request.researchRoom && (
                <span className="profile-collab-research-room">
                  <FaProjectDiagram /> {request.researchRoom.name}
                </span>
              )}
            </div>
          </div>
        </Link>
        {type === "incoming" && (
          <div className="profile-collab-request-actions">
            <button
              className={`profile-collab-accept-btn ${
                isProcessing === request.id ? "profile-collab-btn-loading" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                onAccept(request.id);
              }}
              disabled={isProcessing === request.id}
            >
              {isProcessing === request.id ? (
                <FaSpinner className="profile-collab-spinner" />
              ) : (
                <FaCheck />
              )}
              Accept
            </button>
            <button
              className={`profile-collab-reject-btn ${
                isProcessing === request.id ? "profile-collab-btn-loading" : ""
              }`}
              onClick={(e) => {
                e.preventDefault();
                onReject(request.id);
              }}
              disabled={isProcessing === request.id}
            >
              {isProcessing === request.id ? (
                <FaSpinner className="profile-collab-spinner" />
              ) : (
                <FaTimesCircle />
              )}
              Reject
            </button>
          </div>
        )}
        {type === "outgoing" && (
          <div className="profile-collab-request-status">
            <span className="profile-collab-status-pending">Pending</span>
          </div>
        )}
      </div>
    );
  }
);

const CollaboratorsModal = ({
  showModal,
  closeModal,
  isOwnProfile,
  currentUser,
  profileUserId,
  onCollaboratorsChange, // New prop for refreshing parent stats
}) => {
  const { user } = useUserContext();
  const [activeTab, setActiveTab] = useState("collaborators");
  const [collaborators, setCollaborators] = useState([]);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(null);
  const [sendingRequests, setSendingRequests] = useState(new Set());
  const [showCollaborationRequestModal, setShowCollaborationRequestModal] =
    useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState(null);
  const [pagination, setPagination] = useState({});
  const [sortBy, setSortBy] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);

  const userId = profileUserId || user?.id;

  useEffect(() => {
    if (showModal && userId) {
      if (activeTab === "collaborators") {
        fetchCollaborators(1); // Reset to page 1 when tab changes
      } else if (activeTab === "requests") {
        fetchRequests();
      }
    }
  }, [showModal, activeTab, userId]);

  // Separate effect for sorting changes
  useEffect(() => {
    if (showModal && userId && activeTab === "collaborators") {
      fetchCollaborators(1); // Reset to page 1 when sorting changes
    }
  }, [sortBy]);

  const fetchCollaborators = async (page = currentPage) => {
    try {
      setIsLoading(true);

      // Use the new optimized collaborators list endpoint
      const data = await collaboratorsAPI.collaboration.getCollaboratorsList(
        userId,
        page,
        20, // limit
        sortBy
      );

      // Add safety check for data structure
      if (!data || !data.collaborators) {
setCollaborators([]);
        setPagination({ totalCount: 0, currentPage: 1, totalPages: 0 });
        return;
      }

      // Transform the new API response to match the existing component structure
      const collaboratorGroups = data.collaborators.map((collab) => ({
        person: {
          id: collab.collaboratorId,
          name: collab.collaborator.name,
          profilePhotoUrl: collab.collaborator.profilePhotoUrl,
          professionalRole: collab.collaborator.professionalRole,
          institutionEmail: null, // Not provided in new API
          field: collab.collaborator.field,
          isVerified: collab.collaborator.isVerified,
          averageRating: collab.collaborator.averageRating,
          totalRatings: collab.collaborator.totalRatings,
          isOpenForCollaboration: collab.collaborator.isOpenForCollaboration,
          relationship: collab.relationship,
        },
        collaborations: collab.researchRooms.map((room, index) => ({
          id: `${collab.collaboratorId}-${index}`,
          roomName: room.name,
          roomId: room.id,
          collaborationType: room.subField || "Research",
          date: collab.timeline.lastCollaboratedAt,
          createdAt: collab.timeline.firstCollaboratedAt,
          status: "accepted",
        })),
        stats: collab.collaborationStats,
        timeline: collab.timeline,
      }));

      setCollaborators(collaboratorGroups);
      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (error) {
toast.error("Failed to load collaborators");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const data = await collaboratorsAPI.collaboration.getPendingRequests(
        1,
        20 // Max limit is 20 according to backend validation
      );

      setRequests(data.requests || []);
    } catch (error) {
toast.error("Failed to load collaboration requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCollaborationRequest = async (collaboratorId) => {
    // Open collaboration request modal
    setSelectedCollaboratorId(collaboratorId);
    setShowCollaborationRequestModal(true);
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      setIsProcessing(requestId);
      await collaboratorsAPI.collaboration.respondToCollaborationRequest(
        requestId,
        "accepted",
        "I'd love to collaborate with you!"
      );
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast.success("Collaboration request accepted");

      // Refresh the collaborators count in the parent component
      if (onCollaboratorsChange) {
        await onCollaboratorsChange();
      }
    } catch (error) {
toast.error("Failed to accept request");
    } finally {
      setIsProcessing(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      setIsProcessing(requestId);
      await collaboratorsAPI.collaboration.respondToCollaborationRequest(
        requestId,
        "rejected",
        "Thank you for the invitation, but I'm currently not available for this collaboration."
      );
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      toast.success("Collaboration request rejected");

      // Refresh the collaborators count in the parent component (in case rejection affects count)
      if (onCollaboratorsChange) {
        await onCollaboratorsChange();
      }
    } catch (error) {
toast.error("Failed to reject request");
    } finally {
      setIsProcessing(null);
    }
  };

  if (!showModal) return null;

  return (
    <div className="profile-collab-modal-overlay" onClick={closeModal}>
      <div
        className="profile-collab-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="profile-collab-modal-header">
          <h2>
            {activeTab === "collaborators" ? (
              <>
                <FaUserFriends />{" "}
                {isOwnProfile ? "My Collaborators" : "Collaborators"}
              </>
            ) : (
              <>
                <FaInbox /> Collaboration Requests
              </>
            )}
          </h2>
          <button className="profile-collab-close-btn" onClick={closeModal}>
            <FaTimes />
          </button>
        </div>

        <div className="profile-collab-modal-tabs">
          <button
            className={`profile-collab-tab-btn ${
              activeTab === "collaborators" ? "active" : ""
            }`}
            onClick={() => setActiveTab("collaborators")}
          >
            <FaUserFriends />
            Collaborators ({pagination.totalCount || collaborators.length})
          </button>
          {isOwnProfile && (
            <button
              className={`profile-collab-tab-btn ${
                activeTab === "requests" ? "active" : ""
              }`}
              onClick={() => setActiveTab("requests")}
            >
              <FaInbox />
              Requests ({requests.length})
            </button>
          )}
        </div>

        <div className="profile-collab-modal-content">
          {isLoading ? (
            <div className="profile-collab-loading-state">
              <FaSpinner className="profile-collab-spinner" />
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === "collaborators" && (
                <>
                  {/* Sorting Controls */}
                  <div className="profile-collab-controls">
                    <div className="profile-collab-sort-controls">
                      <label>Sort by:</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="profile-collab-sort-select"
                      >
                        <option value="recent">Recent Collaborations</option>
                        <option value="frequent">Most Frequent</option>
                        <option value="rating">Highest Rated</option>
                        <option value="name">Alphabetical</option>
                      </select>
                    </div>
                    {pagination.totalCount > 0 && (
                      <div className="profile-collab-count">
                        {pagination.totalCount} collaborator
                        {pagination.totalCount !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>

                  <div className="profile-collab-list">
                    {collaborators.length === 0 ? (
                      <div className="profile-collab-empty-state">
                        <FaUserFriends />
                        <p>No collaborators yet</p>
                        <Link
                          to="/collaborators"
                          className="profile-collab-discover-btn"
                        >
                          Discover Collaborators
                        </Link>
                      </div>
                    ) : (
                      collaborators.map((collaboratorGroup) => (
                        <CollaboratorGroupItem
                          key={collaboratorGroup.person.id}
                          collaboratorGroup={collaboratorGroup}
                          isOwnProfile={isOwnProfile}
                          currentUser={currentUser}
                          onSendRequest={handleSendCollaborationRequest}
                          isSendingRequest={sendingRequests.has(
                            collaboratorGroup.person.id
                          )}
                        />
                      ))
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {pagination.totalPages > 1 && (
                    <div className="profile-collab-pagination">
                      <button
                        onClick={() => fetchCollaborators(currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className="profile-collab-page-btn"
                      >
                        Previous
                      </button>
                      <span className="profile-collab-page-info">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => fetchCollaborators(currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="profile-collab-page-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}

              {activeTab === "requests" && isOwnProfile && (
                <div className="profile-collab-requests-list">
                  {requests.length === 0 ? (
                    <div className="profile-collab-empty-state">
                      <FaInbox />
                      <p>No pending collaboration requests</p>
                    </div>
                  ) : (
                    requests.map((request) => (
                      <RequestItem
                        key={request.id}
                        request={request}
                        onAccept={handleAcceptRequest}
                        onReject={handleRejectRequest}
                        isProcessing={isProcessing}
                        type="incoming"
                        currentUserId={user?.id}
                      />
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="profile-collab-modal-footer">
          <Link to="/collaborators" className="profile-collab-full-link">
            <FaEye /> View Full Collaborators Section
          </Link>
        </div>
      </div>

      {/* Collaboration Request Modal */}
      <CollabRequestModal
        isOpen={showCollaborationRequestModal}
        onClose={() => {
          setShowCollaborationRequestModal(false);
          setSelectedCollaboratorId(null);
        }}
        targetUser={{
          id: selectedCollaboratorId,
          name: selectedCollaboratorId
            ? collaborators.find((c) => c.id === selectedCollaboratorId)
                ?.name || "User"
            : "User",
        }}
        onRequestSent={() => {
          // Optional: Handle request sent callback
}}
      />
    </div>
  );
};

export default CollaboratorsModal;
